/**
 * Assign M targets to N stud positions minimising Σ |target_i − stud_chosen_i|,
 * returning each target's stud index, strictly increasing so no two pairs
 * cross. Both arrays must already be sorted ascending — nothing checks it.
 */
export function pickOrderedAssignment(
  targets: number[],
  studs: number[],
): number[] {
  const M = targets.length;
  const N = studs.length;
  if (M === 0 || N < M) return [];
  const INF = Number.POSITIVE_INFINITY;
  // dp[i][j] = min cost placing first i targets using studs from [0..j).
  const dp: number[][] = Array.from({ length: M + 1 }, () =>
    new Array(N + 1).fill(INF),
  );
  // -1 = skip stud j-1, ≥0 = place target i-1 at stud index choice[i][j].
  const choice: number[][] = Array.from({ length: M + 1 }, () =>
    new Array(N + 1).fill(-2),
  );
  for (let j = 0; j <= N; j++) dp[0][j] = 0;
  for (let i = 1; i <= M; i++) {
    for (let j = i; j <= N; j++) {
      if (dp[i][j - 1] < dp[i][j]) {
        dp[i][j] = dp[i][j - 1];
        choice[i][j] = -1;
      }
      const cost = Math.abs(targets[i - 1] - studs[j - 1]);
      const cand = dp[i - 1][j - 1] + cost;
      if (cand < dp[i][j]) {
        dp[i][j] = cand;
        choice[i][j] = j - 1;
      }
    }
  }
  const out: number[] = new Array(M);
  let i = M;
  let j = N;
  while (i > 0 && j > 0) {
    const c = choice[i][j];
    if (c === -1) {
      j -= 1;
    } else {
      out[i - 1] = c;
      i -= 1;
      j -= 1;
    }
  }
  return out;
}
